import React, { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Clock } from "lucide-react";
import { JumpToLatest } from "@/components/JumpToLatest";
import { SegmentItem } from "@/components/SegmentItem";

export const TranscriptPanel = ({ transcript, isRecording, isTranscribing, sessionTitle, startTime, endTime }) => {
  const scrollRef = useRef(null);

  const stickToBottomRef = useRef(true);
  const lastText = transcript.length ? transcript[transcript.length - 1].text : "";

  // Auto-scroll only when new text arrives and the user is already at the bottom
  useEffect(() => {
    if (scrollRef.current && stickToBottomRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript.length, lastText]);

  const [showJump, setShowJump] = useState(false);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    stickToBottomRef.current = atBottom;
    setShowJump(!atBottom);
  };

  const jumpToLatest = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  const formatTime = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Card className="bg-deepspace-surface border-l border-deepspace-border h-full flex flex-col overflow-hidden" data-testid="transcript-panel">
      <div className="p-6 border-b border-deepspace-border flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex-1">
            <h2 className="text-2xl font-semibold font-outfit text-[#EDEDED]">Live Transcript</h2>
            {sessionTitle && (
              <p className="text-sm text-[#A1A1AA] mt-1">{sessionTitle}</p>
            )}
          </div>
          {isTranscribing && (
            <div className="flex items-center gap-2 text-volt-blue">
              <div className="w-2 h-2 bg-volt-blue rounded-full animate-pulse"></div>
              <span className="text-xs font-medium">Processing...</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-6 text-xs text-[#A1A1AA] mt-3 border-t border-deepspace-border pt-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-volt-blue" />
            <div>
              <span className="text-[#52525B]">Start:</span> {formatTime(startTime)}
            </div>
          </div>
          {endTime && (
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-recording-red" />
              <div>
                <span className="text-[#52525B]">End:</span> {formatTime(endTime)}
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-[#52525B]">Segments:</span>
            <span className="text-volt-blue font-semibold">{transcript.length}</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden relative">
        <JumpToLatest visible={showJump && transcript.length > 0} onClick={jumpToLatest} testId="jump-to-latest-btn" />
        <div className="h-full transcript-container p-6" ref={scrollRef} onScroll={handleScroll} style={{ overflowY: 'scroll' }} data-testid="transcript-scroll-area">
          {transcript.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center" data-testid="transcript-empty-state">
              <div className="text-[#52525B] mb-4">
                <svg className="w-20 h-20 mx-auto mb-4 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <p className="text-[#A1A1AA] text-lg">
                {isRecording ? "Listening for audio..." : "Start recording to see transcript"}
              </p>
              <p className="text-[#52525B] text-sm mt-2">
                Transcripts are generated every ~90 seconds
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {transcript.map((item, index) => (
                <SegmentItem key={item.id || index} item={item} index={index} />
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default TranscriptPanel;
